import { useCallback, useMemo, useState } from 'react';

type useHasPaneScrollTypes = {
  paneIds?: string[];
};

export const useHasPaneScrolled = ({ paneIds }: useHasPaneScrollTypes) => {
  const hasCustomPaneIds = paneIds && paneIds.length;
  const initialPaneState = hasCustomPaneIds
    ? paneIds.reduce((acc, id) => ({ ...acc, [id]: false }), {})
    : false;

  const [paneScrollState, setPaneScrollState] = useState<
    Record<string, boolean> | boolean
  >(initialPaneState);

  const handleOnScroll = useCallback(
    (scrollTop: number, paneId?: string) => {
      const hasPaneScrolled = scrollTop > 0;

      if (hasCustomPaneIds && paneId) {
        setPaneScrollState((prev) => ({
          ...(prev as Record<string, boolean>),
          [paneId]: hasPaneScrolled,
        }));
      } else {
        setPaneScrollState(hasPaneScrolled);
      }
    },
    [setPaneScrollState, hasCustomPaneIds]
  );

  const hasAnyPaneScrolled = useMemo(() => {
    if (hasCustomPaneIds) {
      return Object.values(paneScrollState as Record<string, boolean>).some(
        (value) => value === true
      );
    }

    return Boolean(paneScrollState);
  }, [paneScrollState, hasCustomPaneIds]);

  const resetPaneScrollState = useCallback(() => {
    setPaneScrollState(initialPaneState);
  }, [initialPaneState]);

  return {
    paneScrollState,
    handleOnScroll,
    hasAnyPaneScrolled,
    resetPaneScrollState,
  };
};
